import { useEffect, useState } from 'react';
import { useTranslate } from 'react-admin';
import { Alert } from '@mui/material';

import { OFFICIAL_VAULTS_NS as NS } from './constants';
import { ApplicationView, isUnappliedApproval, replaceWaitSeconds } from './helpers';

/**
 * STORY-22 T-02b: the dialog is about to replace an approval that has not taken effect. The engine refuses a
 * replacement inside the 60 s quiet period, so the notice counts that period down from `review.at`; the engine's own
 * `retryAfter` on a refusal is the authority and resets the countdown.
 */
export const ReplaceApprovalNotice = ({
  request,
  retryAfter,
}: {
  request: Pick<ApplicationView, 'review' | 'vaultState'> | null | undefined;
  /** Seconds the engine said to wait on the last refusal, if any. */
  retryAfter?: number;
}) => {
  const translate = useTranslate();
  const unapplied = Boolean(request && isUnappliedApproval(request));
  const [now, setNow] = useState(() => Date.now());
  const [until, setUntil] = useState<number | null>(null);

  useEffect(() => {
    if (typeof retryAfter === 'number') setUntil(Date.now() + retryAfter * 1000);
  }, [retryAfter]);

  const localWait = request ? replaceWaitSeconds(request, now) : 0;
  const engineWait = until === null ? 0 : Math.max(0, Math.ceil((until - now) / 1000));
  const seconds = Math.max(localWait, engineWait);

  useEffect(() => {
    if (!unapplied || seconds <= 0) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [unapplied, seconds]);

  if (!unapplied) return null;
  return (
    <Alert severity={seconds > 0 ? 'warning' : 'info'}>
      {seconds > 0
        ? translate(`${NS}.review.replaceWait`, { seconds })
        : translate(`${NS}.review.replaceReady`)}
    </Alert>
  );
};
